import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { PortalNavbarComponent } from './portal-navbar.component';
import { TeamDto, TeamService } from '../services/team.service';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-utilities-teams-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>
      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/home" class="home-btn">Home</a>
          <h1 class="page-title">Utilities · Teams Data Maintenance</h1>
          <p class="page-sub">All team records for maintenance operations.</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="card">
            <div class="card-hd">
              <span class="card-title">Teams</span>
              <div class="card-tools">
                <input class="tool-input" placeholder="Search teams…" [(ngModel)]="filterText" />
                <span class="card-count">{{ filteredTeams().length }} team{{ filteredTeams().length !== 1 ? 's' : '' }}</span>
              </div>
            </div>

            <div class="table-wrap">
              <div class="table-head" [class.table-head--with-actions]="canHardDeleteTeams">
                <span>Name</span>
                <span>Organization</span>
                <span>Color</span>
                <span>Enabled</span>
                <span>Deleted</span>
                @if (canHardDeleteTeams) {
                  <span>Actions</span>
                }
              </div>

              @for (team of filteredTeams(); track team.id) {
                <div class="table-row" [class.table-row--with-actions]="canHardDeleteTeams">
                  <span>{{ team.name }}</span>
                  <span>{{ getOrgName(team.organizationId) }}</span>
                  <span>{{ team.color || '—' }}</span>
                  <span>{{ team.isEnabled ? 'Yes' : 'No' }}</span>
                  <span>{{ team.isDeleted ? 'Yes' : 'No' }}</span>
                  @if (canHardDeleteTeams) {
                    <span>
                      @if (team.isDeleted) {
                        <button class="hard-delete-btn" type="button" (click)="hardDeleteTeam(team)">Hard Delete</button>
                      } @else {
                        <span class="hard-delete-placeholder">—</span>
                      }
                    </span>
                  }
                </div>
              } @empty {
                <div class="empty">No teams found.</div>
              }
            </div>
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`
    .page { min-height: 100vh; }
    .page-hero {
      border-bottom: 1px solid var(--color-border);
      padding: 2rem 1.5rem;
      background: linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
    }
    .page-hero-inner,
    .page-body-inner { max-width: 1180px; margin: 0 auto; }
    .home-btn { color: var(--color-muted); text-decoration: none; }
    .page-title { margin: .6rem 0 0; }
    .page-sub { margin: .4rem 0 0; color: var(--color-muted); }
    .page-body { padding: 1.5rem; }
    .card {
      border: 1px solid var(--color-border);
      border-radius: .9rem;
      overflow: hidden;
      background: color-mix(in srgb, var(--color-panel) 80%, transparent);
    }
    .card-hd {
      display: flex;
      justify-content: space-between;
      gap: .6rem;
      flex-wrap: wrap;
      border-bottom: 1px solid var(--color-border);
      padding: .8rem 1rem;
    }
    .card-title { font-weight: 600; }
    .card-tools { display: flex; gap: .45rem; align-items: center; }
    .tool-input {
      border: 1px solid var(--color-border);
      border-radius: .45rem;
      background: transparent;
      color: var(--color-text);
      padding: .38rem .5rem;
    }
    .card-count { color: var(--color-muted); font-size: .9rem; }
    .table-head,
    .table-row {
      display: grid;
      grid-template-columns: 1.4fr 1fr .8fr .7fr .7fr;
      gap: .7rem;
      padding: .7rem 1rem;
      align-items: center;
    }
    .table-head--with-actions,
    .table-row--with-actions {
      grid-template-columns: 1.4fr 1fr .8fr .7fr .7fr .8fr;
    }
    .table-head {
      font-size: .72rem;
      text-transform: uppercase;
      letter-spacing: .05em;
      color: var(--color-muted);
      border-bottom: 1px solid var(--color-border);
    }
    .table-row {
      border-bottom: 1px solid color-mix(in srgb, var(--color-border) 50%, transparent);
    }
    .hard-delete-btn {
      background: color-mix(in srgb, #ef4444 16%, transparent);
      border: 1px solid color-mix(in srgb, #ef4444 32%, transparent);
      border-radius: .4rem;
      color: #f87171;
      cursor: pointer;
      font-size: .72rem;
      font-weight: 600;
      padding: .2rem .45rem;
    }
    .hard-delete-btn:hover {
      background: color-mix(in srgb, #ef4444 24%, transparent);
      border-color: color-mix(in srgb, #ef4444 45%, transparent);
    }
    .hard-delete-placeholder {
      color: var(--color-muted);
      opacity: .7;
    }
    .empty { padding: 1rem; color: var(--color-muted); }
  `]
})
export class UtilitiesTeamsPageComponent implements OnInit {
  private readonly teamService = inject(TeamService);
  private readonly organizationService = inject(OrganizationService);
  private readonly authService = inject(AuthService);

  readonly teams = signal<TeamDto[]>([]);
  readonly organizations = signal<OrganizationDto[]>([]);
  filterText = '';
  canHardDeleteTeams = false;

  readonly filteredTeams = computed(() => {
    const q = this.filterText.trim().toLowerCase();
    return this.teams()
      .filter(t => !q || t.name.toLowerCase().includes(q) || this.getOrgName(t.organizationId).toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  ngOnInit(): void {
    void this.initializeAccess();
    this.loadData();
  }

  private async initializeAccess(): Promise<void> {
    const canHardDeleteTeams = await this.authService.hasRoleAsync('SiteAdmin');
    window.setTimeout(() => {
      this.canHardDeleteTeams = canHardDeleteTeams;
    }, 0);
  }

  private loadData(): void {
    forkJoin({ teams: this.teamService.getAll(), organizations: this.organizationService.getAll() }).subscribe({
      next: ({ teams, organizations }) => { this.teams.set(teams); this.organizations.set(organizations); }
    });
  }

  hardDeleteTeam(team: TeamDto): void {
    const confirmed = window.confirm(`Permanently delete team "${team.name}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    this.teamService.hardDelete(team.id).subscribe({
      next: () => {
        this.teams.update(list => list.filter(t => t.id !== team.id));
      },
      error: (err) => {
        console.error('Failed to hard delete team:', err);
      }
    });
  }

  getOrgName(organizationId: number): string {
    return this.organizations().find(o => o.id === organizationId)?.displayName || `Org ${organizationId}`;
  }
}
